import { Clock, Download } from 'lucide-react';
import { useState } from 'react';

import { useAmbientesControllerListar } from '@/api/generated/admin-ambientes/admin-ambientes';
import { useRelatoriosControllerTempoAtivo } from '@/api/generated/admin-relatorios/admin-relatorios';
import {
  FiltroDeUf,
  TODAS_AS_UFS,
  type ValorDoFiltroDeUf,
} from '@/features/admin/relatorios/components/FiltroDeUf';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ExportarCsvDialogo } from '@/shared/components/ExportarCsvDialogo';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/** "125" → "2h05". Minutos ativos não costuma passar de poucas horas por dia. */
function rotuloMinutos(minutos: number): string {
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto} min`;
  return `${horas}h${String(resto).padStart(2, '0')}`;
}

function rotuloPeriodo(desde: string, ate: string): string {
  const formatar = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR');
  return `${formatar(desde)} até ${formatar(ate)}`;
}

/**
 * "Tempo ativo" — minutos somados a partir do heartbeat do frontend
 * (`useHeartbeatDeAtividade`). Sem `desde`/`ate`, o servidor usa os últimos 7
 * dias — os campos aqui só entram quando a pessoa quer outro intervalo.
 * "Logins" conta as entradas no mesmo período.
 */
export function TempoAtivoTab() {
  const { pode } = usePermissoes();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [ambienteId, setAmbienteId] = useState<string>(TODOS);
  const [uf, setUf] = useState<ValorDoFiltroDeUf>(TODAS_AS_UFS);
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [exportando, setExportando] = useState(false);

  const ufFiltrada = uf !== TODAS_AS_UFS ? uf : undefined;

  const { data, isPending, isError, refetch } = useRelatoriosControllerTempoAtivo({
    pagina,
    porPagina: 20,
    ordenarPor: 'minutosAtivos',
    ordem: 'desc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(ambienteId !== TODOS ? { tenantId: ambienteId } : {}),
    ...(ufFiltrada ? { uf: ufFiltrada } : {}),
    ...(desde ? { desde } : {}),
    ...(ate ? { ate } : {}),
  });

  const { data: ambientes } = useAmbientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: pode('admin_tenants.visualizar') } },
  );

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome ou e-mail..."
            className="sm:max-w-xs"
          />

          {ambientes && (
            <Select value={ambienteId} onValueChange={(v) => aoFiltrar(() => setAmbienteId(v))}>
              <SelectTrigger className="sm:w-52" aria-label="Filtrar por ambiente">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os ambientes</SelectItem>
                {ambientes.itens.map((ambiente) => (
                  <SelectItem key={ambiente.id} value={ambiente.id}>
                    {ambiente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <FiltroDeUf valor={uf} aoMudar={(v) => aoFiltrar(() => setUf(v))} />

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={desde}
              onChange={(e) => aoFiltrar(() => setDesde(e.target.value))}
              aria-label="Desde"
              className="w-40"
            />
            <span className="text-muted-foreground text-sm">até</span>
            <Input
              type="date"
              value={ate}
              onChange={(e) => aoFiltrar(() => setAte(e.target.value))}
              aria-label="Até"
              className="w-40"
            />
          </div>
        </div>

        <Button variant="outline" size="sm" className="gap-2" onClick={() => setExportando(true)}>
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Tempo ativo{data ? ` — ${rotuloPeriodo(data.desde, data.ate)}` : ''}
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Clock}
            titulo="Nenhuma atividade no período"
            descricao="Ninguém usou o sistema no intervalo escolhido, ou os filtros não bateram com nenhum usuário."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>UF</TableHead>
                    <TableHead className="text-right">Logins</TableHead>
                    <TableHead className="text-right">Tempo ativo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((item) => (
                    <TableRow key={item.profileId}>
                      <TableCell className="text-foreground">{item.nomeCompleto}</TableCell>
                      <TableCell className="text-muted-foreground">{item.uf ?? '—'}</TableCell>
                      <TableCell className="text-right text-foreground">{item.logins}</TableCell>
                      <TableCell className="text-right font-semibold text-brand">
                        {rotuloMinutos(item.minutosAtivos)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <ExportarCsvDialogo
        aberto={exportando}
        aoFechar={() => setExportando(false)}
        titulo="Exportar tempo ativo"
        caminho="/v1/admin/relatorios/tempo-ativo/exportar.csv"
        descricaoCompleto="Soma o HISTÓRICO INTEIRO de cada usuário, ignorando busca, ambiente, estado e o período selecionado na tela — diferente da tela, que sem período mostra só os últimos 7 dias."
        descricaoPeriodo="Aplica o recorte da tela (busca, ambiente e estado) e limita aos minutos somados no intervalo escolhido abaixo (os dois lados são opcionais)."
        filtros={{
          busca: busca.trim() || undefined,
          tenantId: ambienteId !== TODOS ? ambienteId : undefined,
          uf: ufFiltrada,
        }}
      />
    </div>
  );
}
